/**
 * Manual Refunds — the outstanding-money view inside Refund Requests.
 *
 * A COD order collected at the door, in cash or on the business's static UPI
 * QR, never passed through Razorpay. Refunding it therefore has no gateway
 * payment to reverse: the money is still with the business until a person
 * sends it back. This is the list of those, and the button that closes one out.
 *
 * Kept as its own component rather than another branch inside RefundRequests
 * because it is a different record entirely — a refund request is what the
 * customer asked for, this is what the business owes.
 */

import React, { useState, useEffect, useCallback } from 'react';
import PropTypes from 'prop-types';
import { CheckCircle, Loader2, Phone, Banknote, Smartphone, HandCoins } from 'lucide-react';
import paymentService from '../../services/paymentService';

const formatCurrency = (amount) =>
  `₹${Number(amount || 0).toLocaleString('en-IN', { minimumFractionDigits: 2 })}`;

const formatDate = (dateStr) =>
  dateStr
    ? new Date(dateStr).toLocaleString('en-IN', {
        day: 'numeric', month: 'short', year: 'numeric',
        hour: '2-digit', minute: '2-digit', timeZone: 'Asia/Kolkata',
      })
    : '—';

/**
 * How the customer paid is how they expect it back, so the instrument is shown
 * as an instruction rather than a label: cash at the door goes back as cash, a
 * UPI-QR payment to their UPI id.
 */
function InstrumentChip({ instrument }) {
  if (instrument === 'upi') {
    return (
      <span className="inline-flex items-center gap-1 px-2 py-1 rounded-full text-xs font-medium bg-indigo-50 text-indigo-700">
        <Smartphone className="w-3 h-3" />
        Return via UPI
      </span>
    );
  }
  if (instrument === 'cash') {
    return (
      <span className="inline-flex items-center gap-1 px-2 py-1 rounded-full text-xs font-medium bg-amber-50 text-amber-700">
        <Banknote className="w-3 h-3" />
        Return as cash
      </span>
    );
  }
  return <span className="text-gray-300 text-xs">—</span>;
}

InstrumentChip.propTypes = { instrument: PropTypes.string };

export default function ManualRefunds({ showToast }) {
  const [settled, setSettled] = useState(false);
  const [refunds, setRefunds] = useState([]);
  const [loading, setLoading] = useState(true);
  const [actionLoading, setActionLoading] = useState(null);

  const fetchRefunds = useCallback(async () => {
    setLoading(true);
    const result = await paymentService.getManualRefunds(settled);
    setRefunds(result.data);
    if (!result.success) showToast(result.error, 'error');
    setLoading(false);
  }, [settled, showToast]);

  useEffect(() => { fetchRefunds(); }, [fetchRefunds]);

  const handleMarkSent = async (refund) => {
    const ok = window.confirm(
      `Confirm you have returned ${formatCurrency(refund.amount)} to ` +
      `${refund.customerName || 'the customer'} for ${refund.orderNumber || `order #${refund.orderId}`}.\n\n` +
      'This only records that it was sent — it does not move any money.'
    );
    if (!ok) return;

    setActionLoading(refund.id);
    const result = await paymentService.markManualRefundSent(refund.id);
    setActionLoading(null);
    if (result.success) {
      showToast('Refund marked as sent');
      fetchRefunds();
    } else {
      showToast(result.error, 'error');
    }
  };

  const outstandingTotal = !settled
    ? refunds.reduce((sum, r) => sum + Number(r.amount || 0), 0)
    : 0;

  return (
    <div className="space-y-4">
      <div className="flex items-start justify-between gap-4 flex-wrap">
        <p className="text-sm text-gray-500 max-w-2xl">
          Money collected at the door that has to be returned by hand. These payments never
          went through Razorpay, so there is no gateway refund to wait on &mdash; someone has
          to send the money back, then tick it off here.
        </p>
        <div className="flex gap-1 p-1 bg-gray-100 rounded-xl w-fit">
          <button
            onClick={() => setSettled(false)}
            className={`px-4 py-2 rounded-lg text-sm font-medium transition-all ${
              !settled ? 'bg-white text-gray-900 shadow-sm' : 'text-gray-500 hover:text-gray-700'
            }`}
          >
            Owed
          </button>
          <button
            onClick={() => setSettled(true)}
            className={`px-4 py-2 rounded-lg text-sm font-medium transition-all ${
              settled ? 'bg-white text-gray-900 shadow-sm' : 'text-gray-500 hover:text-gray-700'
            }`}
          >
            Sent
          </button>
        </div>
      </div>

      {!settled && refunds.length > 0 && (
        <div className="flex items-center gap-2 px-4 py-3 bg-amber-50 border border-amber-200 rounded-xl text-amber-800 text-sm">
          <HandCoins className="w-4 h-4 flex-shrink-0" />
          <span>
            <strong>{formatCurrency(outstandingTotal)}</strong> owed across {refunds.length}{' '}
            {refunds.length === 1 ? 'customer' : 'customers'}.
          </span>
        </div>
      )}

      {(() => {
        if (loading) return (
          <div className="flex items-center justify-center py-16 text-gray-400">
            <Loader2 className="w-6 h-6 animate-spin mr-2" />
            Loading…
          </div>
        );
        if (refunds.length === 0) return (
          <div className="flex flex-col items-center justify-center py-16 text-gray-400">
            <CheckCircle className="w-10 h-10 mb-3" />
            <p className="text-sm font-medium">
              {settled ? 'No refunds have been marked as sent yet' : 'Nothing owed — all caught up'}
            </p>
          </div>
        );
        return (
          <div className="bg-white rounded-2xl border border-gray-200 overflow-hidden">
            <div className="overflow-x-auto">
              <table className="w-full text-sm">
                <thead className="bg-gray-50 border-b border-gray-200">
                  <tr>
                    <th className="text-left px-5 py-3 text-xs font-semibold text-gray-500 uppercase tracking-wide">Order</th>
                    <th className="text-left px-5 py-3 text-xs font-semibold text-gray-500 uppercase tracking-wide">Customer</th>
                    <th className="text-left px-5 py-3 text-xs font-semibold text-gray-500 uppercase tracking-wide">Amount</th>
                    <th className="text-left px-5 py-3 text-xs font-semibold text-gray-500 uppercase tracking-wide">Send back</th>
                    <th className="text-left px-5 py-3 text-xs font-semibold text-gray-500 uppercase tracking-wide">
                      {settled ? 'Sent At' : 'Owed Since'}
                    </th>
                    {!settled && (
                      <th className="text-right px-5 py-3 text-xs font-semibold text-gray-500 uppercase tracking-wide">Actions</th>
                    )}
                  </tr>
                </thead>
                <tbody className="divide-y divide-gray-100">
                  {refunds.map((refund) => (
                    <tr key={refund.id} className="hover:bg-gray-50 transition-colors">
                      <td className="px-5 py-4">
                        <p className="font-semibold text-gray-900 text-sm">
                          {refund.orderNumber || `ORD-${refund.orderId}`}
                        </p>
                        <p className="text-xs text-gray-400 mt-0.5">ID #{refund.orderId}</p>
                      </td>

                      <td className="px-5 py-4">
                        {refund.customerName || refund.customerPhone ? (
                          <>
                            <p className="font-medium text-gray-800 text-sm">{refund.customerName || '—'}</p>
                            {refund.customerPhone && (
                              <p className="text-xs text-gray-500 mt-0.5 flex items-center gap-1">
                                <Phone className="w-3 h-3" /> {refund.customerPhone}
                              </p>
                            )}
                          </>
                        ) : (
                          <span className="text-gray-300 text-xs">—</span>
                        )}
                      </td>

                      <td className="px-5 py-4 font-semibold text-gray-900">{formatCurrency(refund.amount)}</td>

                      <td className="px-5 py-4"><InstrumentChip instrument={refund.paymentInstrument} /></td>

                      <td className="px-5 py-4 text-gray-500 text-xs">
                        {formatDate(settled ? refund.processedAt : refund.createdAt)}
                      </td>

                      {!settled && (
                        <td className="px-5 py-4">
                          <div className="flex items-center justify-end">
                            <button
                              onClick={() => handleMarkSent(refund)}
                              disabled={!!actionLoading}
                              className="flex items-center gap-1.5 px-3 py-1.5 bg-green-600 hover:bg-green-700 text-white text-xs font-semibold rounded-lg disabled:opacity-50 transition-colors"
                            >
                              {actionLoading === refund.id
                                ? <Loader2 className="w-3 h-3 animate-spin" />
                                : <CheckCircle className="w-3 h-3" />}
                              Mark as sent
                            </button>
                          </div>
                        </td>
                      )}
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </div>
        );
      })()}
    </div>
  );
}

ManualRefunds.propTypes = { showToast: PropTypes.func.isRequired };
